import { api, getErrorMessage, handleServiceError } from './api';

// ====================================================================
// TIPOS
// ====================================================================

// O backend armazena o mapa conceitual como um objeto JSON genérico
// Vamos definir a estrutura esperada baseada no código do backend
export interface ConceptMapData {
  // Campo obrigatório usado para contexto da geração
  project_idea?: string;

  // Campos gerados pela IA (estrutura do backend)
  publico_alvo?: string;
  problema_central?: string;
  solucao_proposta?: string;
  registro_formalidade?: string;
  pessoa_gramatical_voz?: string;
  diretrizes_estilo?: string;
  tom_de_voz?: string; // legado (Estilo de Redação — ver Framework de Estilos de Redação)
  tom_voz?: string; // alias legado
  linguagem?: string; // legado
  diretrizes_linguagem?: string; // legado
  objetivos_principais?: string[];
  objetivos?: string[];
  principais_diferenciais?: string[];
  diferenciais?: string[];

  // Campos adicionais que podem existir
  [key: string]: any;
}

// ====================================================================
// CONCEPT MAP SERVICE
// ====================================================================

export const conceptMapService = {
  /**
   * Busca o mapa conceitual salvo de uma proposta
   * Retorna um objeto vazio {} se não existir
   */
  getByProposal: async (proposalId: number): Promise<ConceptMapData> => {
    try {
      const response = await api.get(`/concept-map/${proposalId}`);
      return response?.data || {};
    } catch (error) {
      handleServiceError(error, { feature: 'concept_map', action: 'get_by_proposal' });
    }
  },

  /**
   * Gera um novo mapa conceitual usando IA
   * Requer que a proposta tenha Vector Store configurado
   *
   * IMPORTANTE: Geração pode demorar 2-5 minutos dependendo do tamanho dos documentos
   * Por isso usamos timeout de 5 minutos (300000ms) ao invés do padrão de 60s
   */
  generate: async (proposalId: number, reasoningLevel: string = 'default', useWebSearch: boolean = false): Promise<ConceptMapData> => {
    try {
      console.log('[ConceptMapService] Iniciando geração de mapa conceitual para proposta:', proposalId);
      console.log('[ConceptMapService] Timeout configurado: 5 minutos (300000ms)');

      const response = await api.post(`/concept-map/${proposalId}/generate`, { reasoning_level: reasoningLevel, use_web_search: useWebSearch }, {
        timeout: 300000, // 5 minutos em milissegundos
      });

      console.log('[ConceptMapService] Mapa conceitual gerado com sucesso:', response?.data);
      return response?.data || {};
    } catch (error: any) {
      console.error('[ConceptMapService] Erro ao gerar mapa conceitual:', error);

      // Tratamento específico para timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('A geração do mapa está demorando mais que o esperado. Por favor, aguarde alguns minutos e recarregue a página para ver se o mapa foi gerado.');
      }

      // Tratamento específico para rate limit
      if (error.response?.status === 429) {
        throw new Error('Muitas solicitações à IA no momento. Por favor, aguarde 2-3 minutos antes de tentar novamente.');
      }

      handleServiceError(error, { feature: 'concept_map', action: 'generate' });
    }
  },

  /**
   * Extrai o mapa conceitual a partir de um documento anexado (fluxo secundário/opcional).
   * Preserva o project_idea já salvo.
   */
  extractFromDocument: async (proposalId: number, documentId: number, reasoningLevel: string = 'default'): Promise<ConceptMapData> => {
    try {
      const response = await api.post(`/concept-map/${proposalId}/extract-from-document`, {
        document_id: documentId,
        reasoning_level: reasoningLevel,
      }, {
        timeout: 180000, // 3 minutos - extração de documentos pode demorar
      });
      return response?.data || {};
    } catch (error) {
      handleServiceError(error, { feature: 'concept_map', action: 'extract_from_document' });
    }
  },

  /**
   * Salva (atualiza) o mapa conceitual editado pelo usuário
   */
  save: async (proposalId: number, data: ConceptMapData): Promise<{ message: string }> => {
    try {
      const response = await api.put(`/concept-map/${proposalId}`, data);
      return response?.data;
    } catch (error) {
      handleServiceError(error, { feature: 'concept_map', action: 'save' });
    }
  },
};
